import { type VariantProps, cva } from 'class-variance-authority';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { cn } from '~/styles/extended-theme.ts';

import { Logo } from '../logos/logo.tsx';
import { AppDrawer } from '../nav/app-drawer.tsx';
import MobileNavLink from '../nav/mobile-nav-link.tsx';
import NavbarLink from '../nav/navbar-link.tsx';
import { Button } from '../ui/button.tsx';
import { Icon } from '../ui/icon.tsx';

const variants = cva('', {
  variants: {
    variant: {
      transparent:
        'tablet:bg-transparent tablet:transition-colors tablet:ease-in-out tablet:duration-300 border-b-0',
      dark: '',
    },
  },
  defaultVariants: {
    variant: 'dark',
  },
});

type Props = {
  hideSignIn?: boolean;
} & VariantProps<typeof variants>;

export function PublicNavbar({ hideSignIn = false, variant }: Props) {
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [windowIsScrolledDown, setWindowIsScrolledDown] = useState(false);
  const onSignInClick = () => navigate('/login');
  // const { state } = useNavigation();

  useEffect(() => {
    const handleScroll = () => {
      setWindowIsScrolledDown(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // React.useEffect(() => {
  //   if (state !== 'idle')
  //     // close when navigating
  //     setIsDrawerOpen(false);
  // }, [state])

  return (
    <>
      {/* phone drawer */}
      <AppDrawer open={isDrawerOpen}>
        <div className="flex flex-col gap-8">
          {hideSignIn ? null : (
            <Button variant="secondary" size="lg" onClick={onSignInClick}>
              SIGN IN
            </Button>
          )}
          <div className="px-2">
            <MobileNavLink route="/gym/" label="Find Gyms & Classes" />
          </div>
          <div className="px-2">
            <MobileNavLink route="/events/" label="Find Events" />
          </div>
        </div>
      </AppDrawer>

      {/* phone */}
      <nav
        className={cn(
          'sticky top-0 z-50 w-full rounded-b-2xl border-b-4 border-b-secondary bg-primary px-6 py-2 text-zinc-500 shadow-sm tablet:px-12 tablet:py-0',
          variants({ variant }),
          windowIsScrolledDown ? 'tablet:bg-primary' : null,
        )}
      >
        <div className="z-50 flex items-center gap-8 tablet:hidden">
          <div className="text-primary-foreground">
            {isDrawerOpen ? (
              <Icon name="x" size="lg" onClick={() => setIsDrawerOpen(false)} />
            ) : (
              <Icon name="hamburger" size="lg" onClick={() => setIsDrawerOpen(true)} />
            )}
          </div>
          <div className="ml-auto flex">
            <Link to="/">
              <Logo size="sm" />
            </Link>
          </div>
        </div>

        {/* tablet and up */}
        <div className="hidden w-full items-center gap-8 tablet:flex">
          {/* in case window was resized with drawer open */}
          {isDrawerOpen ? (
            <Icon
              name="x"
              size="lg"
              aria-label="Close navigation menu"
              className="text-primary-foreground"
              onClick={() => setIsDrawerOpen(false)}
            />
          ) : null}
          <Link to="/">
            <Logo size="sm" />
          </Link>
          <NavbarLink route="/gym" label="Find Gyms & Classes" />

          <NavbarLink route="/events" label="Find Events" />

          <div className="ml-auto flex pr-4">
            {hideSignIn ? null : (
              <Button variant="secondary" onClick={onSignInClick}>
                SIGN IN
              </Button>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}
